import { analysisCodesValue, handleCheckValidMargin, handleCheckValidSellingPrice } from "./utils"

export type IMinMarginPriceBypassItem = {
  cost_price_pennies?: number | null
  price_pennies?: number | null
  markUp?: number | null
  stock_code?: string
  product?: {
    minimum_margin?: number | null
    minimum_selling_price_pennies?: number | null
    stock_code?: string
    product_group?: {
      minimum_product_margin?: number | null
      requires_elevated_min_price_approval?: boolean
    } | null
  } | null
}

export type IMinMarginPriceBypassRecord = {
  bypassMinMarginApproved?: boolean
  hasOpportunityCertificate?: boolean
  customer?: {
    internalCustomer?: boolean
    analysisCodes?: { key: string; name: string; value: string }[]
  } | null
}

export function isHouseCustomer(
  customer?: IMinMarginPriceBypassRecord["customer"],
): boolean {
  if (!customer?.analysisCodes) {
    return false
  }
  return (
    analysisCodesValue(customer.analysisCodes, "Account Type").toLowerCase() ===
    "house"
  )
}

export type IMinMarginPriceBypassUser = {
  id: string | null
  isSuperAdmin: boolean
  canApproveByPassMinMargins: boolean
  canApproveElevatedMinPriceBypass: boolean
}

const BANNER_MESSAGE_MARGIN_ONLY = "One or more items have a margin below the allowed minimum."
const BANNER_MESSAGE_PRICE_ONLY =
  "One or more items have a selling price below the allowed minimum."
const BANNER_MESSAGE_PRICE_ELEVATED =
  "One or more items are below the minimum selling price and require elevated approval."
const BANNER_MESSAGE_MARGIN_AND_PRICE =
  "One or more items have a selling price or margin below the allowed minimum."
const BANNER_MESSAGE_MARGIN_AND_PRICE_ELEVATED =
  "One or more items have a selling price or margin below the allowed minimum. Elevated approval is required for some items."

export function priceItemsRequireElevatedApproval(items: IMinMarginPriceBypassItem[]): boolean {
  return items.some(item => !!item.product?.product_group?.requires_elevated_min_price_approval)
}

function getBannerMessage(
  marginFails: boolean,
  priceFails: boolean,
  failingElevatedPriceItems: IMinMarginPriceBypassItem[],
): string {
  const needsElevated = failingElevatedPriceItems.length > 0

  if (marginFails && priceFails) {
    return needsElevated
      ? BANNER_MESSAGE_MARGIN_AND_PRICE_ELEVATED
      : BANNER_MESSAGE_MARGIN_AND_PRICE
  }
  if (priceFails) {
    return needsElevated ? BANNER_MESSAGE_PRICE_ELEVATED : BANNER_MESSAGE_PRICE_ONLY
  }
  return BANNER_MESSAGE_MARGIN_ONLY
}

function getApprovalTooltipMessage({
  bannerMessage,
  disabledByMinMarginApproval,
  disabledByMinPriceApproval,
  failingElevatedPriceItems,
  user,
}: {
  bannerMessage: string
  disabledByMinMarginApproval: boolean
  disabledByMinPriceApproval: boolean
  failingElevatedPriceItems: IMinMarginPriceBypassItem[]
  user: IMinMarginPriceBypassUser
}): string | undefined {
  if (!disabledByMinMarginApproval && !disabledByMinPriceApproval) {
    return undefined
  }

  const needsElevated = failingElevatedPriceItems.length > 0
  if (
    disabledByMinPriceApproval &&
    needsElevated &&
    !user.canApproveElevatedMinPriceBypass &&
    user.canApproveByPassMinMargins
  ) {
    return "One or more items require elevated minimum price bypass approval. You do not have permission to approve this bypass."
  }
  if (
    disabledByMinPriceApproval &&
    needsElevated &&
    !user.canApproveElevatedMinPriceBypass &&
    !user.canApproveByPassMinMargins
  ) {
    return "One or more items require elevated minimum price bypass approval."
  }

  return bannerMessage
}

export function isMarginApprovalBypassedForUser(
  user: { id: string | null; isSuperAdmin: boolean },
  branchDirectorUsers: { id: string }[] = [],
): boolean {
  return (
    !!(user.id && branchDirectorUsers.find(x => x.id == user.id)) ||
    (user.isSuperAdmin && process.env.NODE_ENV !== "development")
  )
}

export function orderExcludedFromMinPriceCheck(record: IMinMarginPriceBypassRecord): boolean {
  return !!(
    record.customer?.internalCustomer ||
    isHouseCustomer(record.customer) ||
    record.hasOpportunityCertificate
  )
}

export function orderExcludedFromMarginCheck(record: IMinMarginPriceBypassRecord): boolean {
  return !!record.customer?.internalCustomer
}

export function itemIsBelowMinimumSellingPrice(item: IMinMarginPriceBypassItem): boolean {
  const stockCode = item.product?.stock_code ?? item.stock_code
  return !!handleCheckValidSellingPrice(
    item.price_pennies,
    item.product?.minimum_selling_price_pennies,
    stockCode,
  )
}

export function itemIsBelowMinimumMargin(item: IMinMarginPriceBypassItem): boolean {
  const stockCode = item.product?.stock_code ?? item.stock_code
  const minMargin =
    item.product?.minimum_margin ?? item.product?.product_group?.minimum_product_margin
  if (item.cost_price_pennies == null || item.price_pennies == null || minMargin == null) {
    return false
  }
  return !!handleCheckValidMargin(
    item.cost_price_pennies,
    item.price_pennies,
    minMargin,
    stockCode,
  )
}

export function getFailingMarginItems<T extends IMinMarginPriceBypassItem>(
  items: T[],
  record: IMinMarginPriceBypassRecord,
): T[] {
  if (orderExcludedFromMarginCheck(record)) {
    return []
  }
  return items.filter(itemIsBelowMinimumMargin)
}

export function getFailingPriceItems<T extends IMinMarginPriceBypassItem>(
  items: T[],
  record: IMinMarginPriceBypassRecord,
): T[] {
  if (orderExcludedFromMinPriceCheck(record)) {
    return []
  }
  return items.filter(itemIsBelowMinimumSellingPrice)
}

export function getFailingElevatedPriceItems<T extends IMinMarginPriceBypassItem>(
  failingPriceItems: T[],
): T[] {
  return failingPriceItems.filter(
    item => !!item.product?.product_group?.requires_elevated_min_price_approval,
  )
}

export function userCanApproveMinPriceBypass(
  user: Pick<
    IMinMarginPriceBypassUser,
    "canApproveByPassMinMargins" | "canApproveElevatedMinPriceBypass"
  >,
  items: IMinMarginPriceBypassItem[],
): boolean {
  const failingItems = items.filter(itemIsBelowMinimumSellingPrice)
  if (failingItems.length === 0) {
    return false
  }
  const needsElevated = getFailingElevatedPriceItems(failingItems).length > 0
  if (needsElevated) {
    return user.canApproveElevatedMinPriceBypass
  }
  return user.canApproveByPassMinMargins
}

export function userCanApproveCombinedBypass(
  user: IMinMarginPriceBypassUser,
  items: IMinMarginPriceBypassItem[],
  record: IMinMarginPriceBypassRecord,
): boolean {
  const failingMarginItems = getFailingMarginItems(items, record)
  const failingPriceItems = getFailingPriceItems(items, record)

  if (failingMarginItems.length === 0 && failingPriceItems.length === 0) {
    return false
  }
  if (failingMarginItems.length > 0 && !user.canApproveByPassMinMargins) {
    return false
  }
  if (failingPriceItems.length > 0 && !userCanApproveMinPriceBypass(user, failingPriceItems)) {
    return false
  }
  return true
}

export function getMinMarginPriceApprovalState({
  items,
  record,
  user,
  branchDirectorUsers,
}: {
  items: IMinMarginPriceBypassItem[]
  record: IMinMarginPriceBypassRecord
  user: IMinMarginPriceBypassUser
  branchDirectorUsers: { id: string }[]
}) {
  const isBranchDirector = isMarginApprovalBypassedForUser(user, branchDirectorUsers)
  const bypassApproved = !!record.bypassMinMarginApproved

  const failingMarginItems = getFailingMarginItems(items, record)
  const failingPriceItems = getFailingPriceItems(items, record)

  const marginFails = failingMarginItems.length > 0
  const priceFails = failingPriceItems.length > 0
  const failingElevatedPriceItems = getFailingElevatedPriceItems(failingPriceItems)
  const elevatedPriceFails = failingElevatedPriceItems.length > 0

  const disabledByMinMarginApproval = marginFails && !isBranchDirector && !bypassApproved

  const disabledByMinPriceApproval =
    priceFails && !bypassApproved && (!isBranchDirector || elevatedPriceFails)
  const isDisabledByMarginOrPrice = disabledByMinMarginApproval || disabledByMinPriceApproval

  const hasAnyFailure = marginFails || priceFails
  const requiresElevatedMinPriceApproval = elevatedPriceFails
  const bannerMessage = hasAnyFailure
    ? getBannerMessage(marginFails, priceFails, failingElevatedPriceItems)
    : undefined
  const bannerType = isDisabledByMarginOrPrice ? ("error" as const) : ("warning" as const)

  const canApproveBypass =
    hasAnyFailure && !bypassApproved && userCanApproveCombinedBypass(user, items, record)

  const approvalTooltipMessage = bannerMessage
    ? getApprovalTooltipMessage({
        bannerMessage,
        disabledByMinMarginApproval,
        disabledByMinPriceApproval,
        failingElevatedPriceItems,
        user,
      })
    : undefined

  const failingItems = items.filter(item => {
    const marginFailing = !orderExcludedFromMarginCheck(record) && itemIsBelowMinimumMargin(item)
    const priceFailing =
      !orderExcludedFromMinPriceCheck(record) && itemIsBelowMinimumSellingPrice(item)
    return marginFailing || priceFailing
  })

  return {
    marginFails,
    priceFails,
    disabledByMinMarginApproval,
    disabledByMinPriceApproval,
    isDisabledByMarginOrPrice,
    bannerMessage,
    bannerType,
    canApproveBypass,
    approvalTooltipMessage,
    failingMarginItems,
    failingPriceItems,
    failingItems,
    requiresElevatedMinPriceApproval,
  }
}

export function shouldResetBypassMinMarginApproved({
  record,
  candidateItems,
  user,
}: {
  record: IMinMarginPriceBypassRecord & { id?: string }
  candidateItems: IMinMarginPriceBypassItem[]
  user: IMinMarginPriceBypassUser
}): boolean {
  if (!record.id || !record.bypassMinMarginApproved) {
    return false
  }
  const failingMarginItems = getFailingMarginItems(candidateItems, record)
  const failingPriceItems = getFailingPriceItems(candidateItems, record)
  if (failingMarginItems.length === 0 && failingPriceItems.length === 0) {
    return false
  }
  return !userCanApproveCombinedBypass(user, candidateItems, record)
}

export function validateMinMarginPriceForExport({
  items,
  record,
  user,
  branchDirectorUsers,
  bypassMinMargin,
}: {
  items: IMinMarginPriceBypassItem[]
  record: IMinMarginPriceBypassRecord
  user: { id: string | null; isSuperAdmin: boolean }
  branchDirectorUsers: { id: string }[]
  bypassMinMargin?: boolean
}) {
  if (bypassMinMargin) {
    return
  }

  const isBranchDirector = isMarginApprovalBypassedForUser(user, branchDirectorUsers)
  const failingMarginItems = getFailingMarginItems(items, record)
  const failingPriceItems = getFailingPriceItems(items, record)
  const failingElevatedPriceItems = getFailingElevatedPriceItems(failingPriceItems)

  if (!isBranchDirector && failingMarginItems.length > 0) {
    throw new Error("One or more order items have a margin below the minimum")
  }
  if (!isBranchDirector && failingPriceItems.length > 0) {
    const needsElevated = failingElevatedPriceItems.length > 0
    throw new Error(
      needsElevated
        ? "One or more order items are below the minimum selling price and require elevated approval"
        : "One or more order items have a selling price below the minimum",
    )
  }
  if (failingElevatedPriceItems.length > 0) {
    throw new Error(
      "One or more order items are below the minimum selling price and require elevated approval",
    )
  }
}
